import PropTypes from "prop-types";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { CircularProgress, ClickAwayListener, Grid, Grow, Paper, Popper } from "@mui/material";
import { useGetSiteFolders } from "../../../hooks";
import FolderTreeContainer from "./FolderTreeContainer";

const findFolder = (folderId, folders) => {
  var found = folders.find(n => n.folderId == folderId);
  for (var i = 0; !found && i < folders.length; i++) {
    found = findFolder(folderId, folders[i].childFolders);
  }
  return found;
};

const findFolderPath = (folderId, folders) => {
  const length = folders.length;
  if (length === 0) {
    return undefined
  }
  const folder = folders.find(n => n.folderId == folderId);
  if (!!folder) {
    return [folder.folderId];
  }
  for (var i = 0; i < length; i++) {
    const folder = folders[i];
    const result = findFolderPath(folderId, folder.childFolders);
    if (!!result) {
      return [folder.folderId, ...result];
    }
  }
  return undefined;
};

const Folders = ({
  initialFolderId,
  onChange,
  onClose,
  onLoading,
  open,
  openedNodes,
  setOpenedNodes,
}) => {
  const folders = useSelector(state => state.api.siteFolders.folders);
  const siteId = useSelector(state => state.ui.toggleWorkspaces.workspace.id);

  const { execute: getSiteFolders, loading } = useGetSiteFolders();

  const handleOpenNode = id => {
    const nodeIndex = openedNodes.indexOf(id);
    if (nodeIndex === -1) {
      setOpenedNodes([...openedNodes, id]);
    } else {
      const tempOpenedNodes = openedNodes.filter(x => x !== id);
      setOpenedNodes(tempOpenedNodes);
    }
  };

  const handleGetSiteFolders = () => {
    getSiteFolders({
      variables: {
        siteId,
      },
    });
  };

  useEffect(() => {
    if ((initialFolderId || open) && siteId && Array.isArray(folders) && folders.length === 0) {
      onLoading?.(true);
      handleGetSiteFolders();
    }
  }, [initialFolderId, open, siteId]);

  useEffect(() => {
    if (Array.isArray(folders) && folders.length > 0) {
      onLoading?.(false);
      if (initialFolderId) {
        const folder = findFolder(initialFolderId, folders);
        if (folder) {
          const folderPath = findFolderPath(initialFolderId, folders);
          setOpenedNodes(folderPath);
          onChange?.(null, folder);
        }
      }
    }
  }, [folders, initialFolderId]);

  return (
    <Popper
      disablePortal
      open={open}
      ref={(node) => node?.scrollIntoView()}
      sx={{
        position: "absolute !important",
        top: "unset !important",
        left: "unset !important",
        width: "100%",
        zIndex: 100,
      }}
      transition={false}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps}>
          <ClickAwayListener onClickAway={onClose}>
            <Paper
              sx={{
                borderRadius: 0.5,
                boxShadow: "1px 2px 7px 1px rgba(0, 0, 0, 0.125)",
                minHeight: 200,
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {loading && (
                <Grid container justifyContent="center">
                  <CircularProgress size={30} />
                </Grid>
              )}
              {!loading && (
                <FolderTreeContainer
                  folders={folders}
                  foundFolders={[initialFolderId]}
                  handleFolderRowClick={onChange}
                  handleOpenNode={handleOpenNode}
                  openedNodes={openedNodes}
                  setOpenedNodes={setOpenedNodes}
                />
              )}
            </Paper>
          </ClickAwayListener>
        </Grow>
      )}
    </Popper>
  );
};

Folders.propTypes = {
  initialFolderId: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onLoading: PropTypes.func,
  open: PropTypes.bool.isRequired,
  openedNodes: PropTypes.arrayOf(PropTypes.number).isRequired,
  setOpenedNodes: PropTypes.func.isRequired,
};

export default Folders;
